"use client";

import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import React from "react";
import { HexColorPicker } from "react-colorful";
import { CircleIcon, Droplet, EraserIcon, InfoIcon, MinusIcon, Paintbrush, PencilIcon, RedoIcon, SquareIcon, Trash2Icon, UndoIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Slider } from "@/components/ui/slider";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
type DrawingTool = "pencil" | "eraser" | "rectangle" | "circle" | "line";
interface DrawingAction {
  tool: DrawingTool;
  points: number[];
  color: string;
  lineWidth: number;
  opacity: number;
}
export interface SketchToolHandle {
  getImageData: () => string | null;
  getShading: () => boolean;
  getSketchFidelity: () => number;
}
const SketchTool = React.memo(forwardRef<SketchToolHandle>((_, ref) => {
  const [tool, setTool] = useState<DrawingTool>("pencil");
  const [color, setColor] = useState("#FFFFFF");
  const [lineWidth, setLineWidth] = useState(5);
  const [opacity, setOpacity] = useState(1);
  const [isDrawing, setIsDrawing] = useState(false);
  const [history, setHistory] = useState<DrawingAction[][]>([[]]);
  const [historyStep, setHistoryStep] = useState(0);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const [shading, setShading] = useState(false);
  const [sketchFidelity, setSketchFidelity] = useState(0.5);
  useImperativeHandle(ref, () => ({
    getImageData: () => {
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext("2d", {
          willReadFrequently: true
        });
        if (ctx) {
          ctx.save();
          // Create a temporary canvas with transparent background
          const tempCanvas = document.createElement("canvas");
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          const tempCtx = tempCanvas.getContext("2d");
          if (tempCtx) {
            // Set composite operation to draw only non-transparent pixels
            tempCtx.globalCompositeOperation = "copy";
            tempCtx.drawImage(canvas, 0, 0);

            // Convert to PNG with transparency and return as base64
            ctx.restore();
            return tempCanvas.toDataURL("image/png");
          }
          ctx.restore();
        }
      }
      return null;
    },
    getShading: () => {
      return shading;
    },
    getSketchFidelity: () => {
      return sketchFidelity;
    }
  }));
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    if (canvas && container) {
      let previousSize = {
        width: 0,
        height: 0
      };
      const resizeCanvas = () => {
        const {
          width,
          height
        } = container.getBoundingClientRect();
        const size = Math.min(width, height);
        if (size !== previousSize.width || size !== previousSize.height) {
          previousSize = {
            width: size,
            height: size
          };

          // Save current content
          const ctx = canvas.getContext("2d");
          if (ctx) {
            const dataUrl = canvas.toDataURL();
            canvas.width = size;
            canvas.height = size;
            canvas.style.width = `${size}px`;
            canvas.style.height = `${size}px`;

            // Restore content
            const img = new Image();
            img.onload = () => {
              ctx.drawImage(img, 0, 0);
            };
            img.src = dataUrl;
          }
        }
      };
      resizeCanvas();
      const resizeObserver = new ResizeObserver(resizeCanvas);
      resizeObserver.observe(container);
      return () => resizeObserver.disconnect();
    }
  }, []);
  useEffect(() => {
    redraw();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history, historyStep]);
  const getPointerPos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const canvas = canvasRef.current;
    if (!canvas) return {
      x: 0,
      y: 0
    };
    const rect = canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  };
  const startDrawing = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const {
      x,
      y
    } = getPointerPos(e);
    setIsDrawing(true);
    const newAction: DrawingAction = {
      tool,
      points: [x, y],
      color,
      lineWidth,
      opacity
    };
    setHistory(prevHistory => {
      const newHistory = [...prevHistory.slice(0, historyStep + 1), [...prevHistory[historyStep], newAction]];
      setHistoryStep(newHistory.length - 1);
      return newHistory;
    });
  };
  const draw = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (!isDrawing) return;
    const {
      x,
      y
    } = getPointerPos(e);
    setHistory(prevHistory => {
      const currentStep = prevHistory[historyStep];
      const lastAction = currentStep[currentStep.length - 1];
      let updatedAction = lastAction;
      if (lastAction.tool === "line" || lastAction.tool === "rectangle" || lastAction.tool === "circle") {
        // Update points for line, rectangle, and circle tools
        updatedAction = {
          ...lastAction,
          points: [lastAction.points[0], lastAction.points[1], x, y]
        };
      } else {
        // For pencil and eraser, add points
        updatedAction = {
          ...lastAction,
          points: [...lastAction.points, x, y]
        };
      }
      const updatedStep = [...currentStep.slice(0, -1), updatedAction];
      const newHistory = [...prevHistory.slice(0, historyStep), updatedStep];
      return newHistory;
    });
    redraw();
  };
  const stopDrawing = () => {
    setIsDrawing(false);
  };
  const redraw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d", {
      willReadFrequently: true
    });
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    history[historyStep].forEach(action => {
      ctx.strokeStyle = action.color;
      ctx.lineWidth = action.lineWidth;
      ctx.globalAlpha = action.opacity;
      if (action.tool === "eraser") {
        ctx.strokeStyle = "#1a1a1a";
      }
      if (action.tool === "pencil" || action.tool === "eraser") {
        ctx.beginPath();
        ctx.moveTo(action.points[0], action.points[1]);
        for (let i = 2; i < action.points.length; i += 2) {
          ctx.lineTo(action.points[i], action.points[i + 1]);
        }
        ctx.stroke();
      } else if (action.tool === "line") {
        // Draw straight line
        if (action.points.length >= 4) {
          ctx.beginPath();
          ctx.moveTo(action.points[0], action.points[1]);
          ctx.lineTo(action.points[2], action.points[3]);
          ctx.stroke();
        }
      } else if (action.tool === "rectangle") {
        // Draw rectangle
        const startX = action.points[0];
        const startY = action.points[1];
        const endX = action.points[action.points.length - 2];
        const endY = action.points[action.points.length - 1];
        const width = endX - startX;
        const height = endY - startY;
        ctx.beginPath();
        ctx.strokeRect(startX, startY, width, height);
      } else if (action.tool === "circle") {
        // Draw circle
        const startX = action.points[0];
        const startY = action.points[1];
        const endX = action.points[action.points.length - 2];
        const endY = action.points[action.points.length - 1];
        const radius = Math.sqrt(Math.pow(endX - startX, 2) + Math.pow(endY - startY, 2));
        ctx.beginPath();
        ctx.arc(startX, startY, radius, 0, 2 * Math.PI);
        ctx.stroke();
      }
      ctx.globalAlpha = 1;
    });
  }, [history, historyStep]);
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = canvasContainerRef.current;
    if (canvas && container) {
      // Observe parent resizing
      const parent = container.parentElement;
      if (parent) {
        const observer = new ResizeObserver(() => {
          redraw();
        });
        observer.observe(parent);
        return () => {
          observer.disconnect();
        };
      }
    }
  }, [redraw]);
  const undo = () => {
    if (historyStep > 0) {
      setHistoryStep(historyStep - 1);
    }
  };
  const redo = () => {
    if (historyStep < history.length - 1) {
      setHistoryStep(historyStep + 1);
    }
  };
  const clearCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#1a1a1a";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHistory([[]]);
    setHistoryStep(0);
  };
  return <Card className="bg-primary text-white border-slate-800 p-[1] sm:p-[1] w-100 h-100">
                <CardContent className="p-[2]">
                    <div className="flex flex-col space-y-1">
                        <div className="flex justify-between items-center">
                            <h2 className="text-xl sm:text-2xl font-bold">
                                Sketch Tool
                            </h2>
                            <div className="flex items-center space-x-1">
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button type="button" onClick={undo} disabled={historyStep === 0} size="icon" variant="ghost" className="text-white hover:text-white hover:bg-slate-950">
                                                <UndoIcon className="w-4 h-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Undo</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button type="button" onClick={redo} disabled={historyStep === history.length - 1} size="icon" variant="ghost" className="text-white hover:text-white hover:bg-slate-950">
                                                <RedoIcon className="w-4 h-4" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Redo</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                                <TooltipProvider>
                                    <Tooltip>
                                        <TooltipTrigger asChild>
                                            <Button type="button" onClick={clearCanvas} size="icon" variant="ghost" className="text-red hover:text-red hover:bg-slate-950">
                                                <Trash2Icon className="w-4 h-4 text-red-600" />
                                            </Button>
                                        </TooltipTrigger>
                                        <TooltipContent>
                                            <p>Clear Canvas</p>
                                        </TooltipContent>
                                    </Tooltip>
                                </TooltipProvider>
                            </div>
                        </div>
                        <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
                            <div className="flex sm:flex-col space-x-2 sm:space-x-0 sm:space-y-2 sm:w-16">
                                <TooltipProvider>
                                    {[{
                icon: PencilIcon,
                toolName: "pencil" as DrawingTool,
                tooltip: "Pencil"
              }, {
                icon: EraserIcon,
                toolName: "eraser" as DrawingTool,
                tooltip: "Eraser"
              }, {
                icon: MinusIcon,
                toolName: "line" as DrawingTool,
                tooltip: "Line"
              }, {
                icon: SquareIcon,
                toolName: "rectangle" as DrawingTool,
                tooltip: "Rectangle"
              }, {
                icon: CircleIcon,
                toolName: "circle" as DrawingTool,
                tooltip: "Circle"
              }].map(({
                icon: Icon,
                toolName,
                tooltip
              }) => <Tooltip key={toolName}>
                                                <TooltipTrigger asChild>
                                                    <Button type="button" onClick={() => setTool(toolName)} variant={tool === toolName ? "secondary" : "ghost"} size="icon" className={`w-full ${tool === toolName ? "bg-secondary text-white" : "text-gray-400 hover:text-gray-100 hover:bg-slate-950"}`}>
                                                        <Icon className="w-4 h-4" />
                                                    </Button>
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>{tooltip}</p>
                                                </TooltipContent>
                                            </Tooltip>)}
                                </TooltipProvider>
                            </div>
                            <div ref={canvasContainerRef} className="flex-grow relative border border-slate-700 rounded-lg overflow-hidden aspect-square w-full h-full">
                                <canvas ref={canvasRef} onPointerDown={startDrawing} onPointerMove={draw} onPointerUp={stopDrawing} onPointerLeave={stopDrawing} className="absolute top-0 left-0 w-full h-full touch-none" />
                            </div>
                        </div>
                        <div className="flex flex-col space-y-4">
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button variant="outline" className="w-full justify-start bg-background text-gray-100 border-slate-800 hover:bg-slate-950 hover:text-gray-100">
                                        <div className="w-5 h-5 rounded-full mr-2" style={{
                  backgroundColor: color
                }} />
                                        Colour
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-[200px] p-3 bg-gray-800 border-gray-700">
                                    <HexColorPicker color={color} onChange={setColor} />
                                </PopoverContent>
                            </Popover>
                            <div className="flex flex-col space-y-4 w-full">
                                <div className="flex items-center space-x-2 w-full">
                                    <Paintbrush className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <Slider id="line-width" min={1} max={100} step={1} value={[lineWidth]} onValueChange={value => setLineWidth(value[0])} className="flex-grow border-white-400 bg-slate-700" />
                                    <span className="text-sm text-gray-400 w-12 text-right">
                                        {lineWidth}px
                                    </span>
                                </div>
                                <div className="flex items-center space-x-2 w-full">
                                    <Droplet className="w-5 h-5 text-gray-400 flex-shrink-0" />
                                    <Slider id="opacity" min={0} max={1} step={0.01} value={[opacity]} onValueChange={value => setOpacity(value[0])} className="flex-grow bg-slate-700" />
                                    <span className="text-sm text-gray-400 w-12 text-right">
                                        {Math.round(opacity * 100)}%
                                    </span>
                                </div>
                                <div className="flex flex-col space-y-4">
                                    <div className="flex items-center space-x-2">
                                        <Checkbox id="shading" checked={shading} onCheckedChange={checked => setShading(checked as boolean)} />
                                        <Label htmlFor="shading">
                                            Include Shading for Softer Details
                                        </Label>
                                        <TooltipProvider>
                                            <Tooltip>
                                                <TooltipTrigger asChild>
                                                    <InfoIcon className="h-4 w-4 ml-2" />
                                                </TooltipTrigger>
                                                <TooltipContent>
                                                    <p>
                                                        Tick to include shading
                                                        for softer details;
                                                        untick for clean
                                                        outlines only.
                                                    </p>
                                                </TooltipContent>
                                            </Tooltip>
                                        </TooltipProvider>
                                    </div>
                                    <div className="flex flex-col space-y-2">
                                        <div className="flex items-center mb-2">
                                            <Label htmlFor="sketch-fidelity">
                                                Sketch Fidelity
                                            </Label>
                                            <TooltipProvider>
                                                <Tooltip>
                                                    <TooltipTrigger asChild>
                                                        <InfoIcon className="h-4 w-4 ml-2" />
                                                    </TooltipTrigger>
                                                    <TooltipContent>
                                                        <p>
                                                            Adjust how closely
                                                            the final image
                                                            should match your
                                                            sketch. Higher
                                                            values result in a
                                                            closer match.
                                                        </p>
                                                    </TooltipContent>
                                                </Tooltip>
                                            </TooltipProvider>
                                        </div>
                                        <Slider id="sketch-fidelity" min={0} max={1} step={0.01} value={[sketchFidelity]} onValueChange={value => setSketchFidelity(value[0])} className="flex-grow bg-slate-700" />
                                        <span className="text-sm text-gray-400 text-right">
                                            {Math.round(sketchFidelity * 100)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </CardContent>
            </Card>;
}));
SketchTool.displayName = "SketchTool";
export default SketchTool;